/**
 * @flow
 */

import Container from 'components/Container';
import Flex from 'components/Flex';
import MarkdownHeader from 'components/MarkdownHeader';
import NavigationFooter from 'components/NavigationFooter';
import React from 'react';
import StickyResponsiveSidebar from 'components/StickyResponsiveSidebar';
import TitleAndMetaTags from 'components/TitleAndMetaTags';
import findSectionForPath from 'utils/findSectionForPath';
import toCommaSeparatedList from 'utils/toCommaSeparatedList';
import { sharedStyles, media } from 'theme';
import createOgUrl from 'utils/createOgUrl';
import { createLinkCreater } from 'utils/createLink';

import Section from './Sidebar/Section';

import type { Node } from 'types';

type Props = {
  authors: Array<string>,
  createLink: Function, // TODO: Add better flow type once we Flow-type createLink
  date?: string,
  header?: string,
  enableScrollSync?: boolean,
  ogDescription: string,
  location: Location,
  markdownRemark: Node,
  sectionList: Array<{ items: Array<{ id: string }>, directory: string }>, // TODO: Add better flow type once we have the Section component
  titlePostfix: string
};

const MarkdownPage = ({
  authors = [],
  createLink,
  date,
  enableScrollSync,
  ogDescription,
  location,
  markdownRemark,
  sectionList,
  titlePostfix = '',
  header
}: Props) => {
  const hasAuthors = authors.length > 0;
  const titlePrefix = markdownRemark.frontmatter.title || '';

  const { prev, next } = (() => {
    if (markdownRemark.frontmatter.next || markdownRemark.frontmatter.prev) {
      const { prev, next } = markdownRemark.frontmatter;
      return { prev, next };
    }
    if (!/^\/(?:ud)?lejer\//.test(location.pathname)) {
      return { prev: null, next: null };
    }
    const items = sectionList.reduce((acc, section) => {
      return section.items
        ? acc.concat(
            section.items.map(item => ({
              ...item,
              url: section.directory
                ? `${section.directory}/${item.id}`
                : item.id
            }))
          )
        : acc;
    }, []);

    const index = items.findIndex(
      item => item.url === markdownRemark.fields.slug
    );

    if (index === -1) {
      return { next: items[0], prev: null };
    }

    const next = index < items.length - 1 ? items[index + 1] : null;
    const prev = index > 0 ? items[index - 1] : null;

    return { prev, next };
  })();

  const mq = media.match(media.lessThan('small'));

  return (
    <Flex
      direction="column"
      grow="1"
      shrink="0"
      halign="stretch"
      css={{
        width: '100%',
        flex: '1 0 auto',
        position: 'relative',
        zIndex: 0
      }}>
      <TitleAndMetaTags
        ogDescription={ogDescription}
        ogUrl={createOgUrl(markdownRemark.fields.slug)}
        title={`${titlePrefix}${titlePostfix}`}
      />
      <div css={{ flex: '1 0 auto' }}>
        <Container>
          <div css={sharedStyles.articleLayout.container}>
            <Flex
              type="article"
              direction="column"
              grow="1"
              halign="stretch"
              css={{ width: '100%' }}>
              {header || <MarkdownHeader title={titlePrefix} />}

              {(date || hasAuthors) && (
                <div css={{ marginTop: 15 }}>
                  {date}{' '}
                  {hasAuthors && (
                    <span>
                      af{' '}
                      {toCommaSeparatedList(authors, author => (
                        <a
                          css={sharedStyles.link}
                          href={author.frontmatter.url}
                          key={author.frontmatter.name}>
                          {author.frontmatter.name}
                        </a>
                      ))}
                    </span>
                  )}
                </div>
              )}

              <div css={sharedStyles.articleLayout.content}>
                <div
                  css={[sharedStyles.markdown]}
                  dangerouslySetInnerHTML={{ __html: markdownRemark.html }}
                />
                {mq && mq.matches && markdownRemark.frontmatter.toc === true ? (
                  <div css={{ marginTop: 40 }}>
                    {sectionList.map((section, index) => (
                      <Section
                        createLink={
                          createLinkCreater(sharedStyles.link) /*createLink*/
                        }
                        // isActive={
                        //   activeSection === section || sectionList.length === 1
                        // }
                        key={index}
                        location={location}
                        // onLinkClick={closeParentMenu}
                        // onSectionTitleClick={() => this._toggleSection(section)}
                        section={section}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </Flex>

            {sectionList.length ? (
              <div css={sharedStyles.articleLayout.sidebar}>
                <StickyResponsiveSidebar
                  enableScrollSync={enableScrollSync}
                  createLink={createLink}
                  defaultActiveSection={findSectionForPath(
                    location.pathname,
                    sectionList
                  )}
                  location={location}
                  sectionList={sectionList}
                />
              </div>
            ) : null}
          </div>
        </Container>
      </div>

      {/* TODO Read prev/next from index map, not this way */}
      {(next || prev) && (
        <NavigationFooter location={location} next={next} prev={prev} />
      )}
    </Flex>
  );
};

export default MarkdownPage;
